import type { App } from "@upsquad/server";
import type { NotificationFilter } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type GetNotificationsResponse = App["~Routes"]["notification"]["get"]["response"]["200"];
export type ApiNotification = GetNotificationsResponse["notifications"][number];
type GetUnreadCountResponse = App["~Routes"]["notification"]["unread-count"]["get"]["response"]["200"];
type MarkAsReadResponse = App["~Routes"]["notification"][":id"]["read"]["patch"]["response"]["200"];
type MarkAsUnreadResponse = App["~Routes"]["notification"][":id"]["unread"]["patch"]["response"]["200"];
type MarkAllAsReadResponse = App["~Routes"]["notification"]["read-all"]["patch"]["response"]["200"];

interface GetNotificationsParams {
	cursor?: string;
	limit?: number;
	filter?: NotificationFilter;
}

export const notificationApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getNotifications: builder.query<GetNotificationsResponse, GetNotificationsParams>({
			query:
				({ cursor, limit, filter }): EdenCall<GetNotificationsResponse> =>
				(client) =>
					client.notification.get({ query: { cursor, limit: limit ?? 20, filter: filter ?? "all" } }),
			providesTags: ["Notifications"],
		}),

		getUnreadCount: builder.query<GetUnreadCountResponse, void>({
			query: (): EdenCall<GetUnreadCountResponse> => (client) => client.notification["unread-count"].get(),
			providesTags: ["UnreadCount"],
		}),

		markAsRead: builder.mutation<MarkAsReadResponse, { id: string }>({
			query:
				({ id }): EdenCall<MarkAsReadResponse> =>
				(client) =>
					client.notification({ id }).read.patch(),
			invalidatesTags: ["Notifications", "UnreadCount"],
		}),

		markAsUnread: builder.mutation<MarkAsUnreadResponse, { id: string }>({
			query:
				({ id }): EdenCall<MarkAsUnreadResponse> =>
				(client) =>
					client.notification({ id }).unread.patch(),
			invalidatesTags: ["Notifications", "UnreadCount"],
		}),

		markAllAsRead: builder.mutation<MarkAllAsReadResponse, void>({
			query: (): EdenCall<MarkAllAsReadResponse> => (client) => client.notification["read-all"].patch({}),
			invalidatesTags: ["Notifications", "UnreadCount"],
		}),
	}),
});

export const {
	useGetNotificationsQuery,
	useGetUnreadCountQuery,
	useMarkAsReadMutation,
	useMarkAsUnreadMutation,
	useMarkAllAsReadMutation,
} = notificationApi;
